import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import LoadingScreen from './components/common/LoadingScreen';
import AccessErrorScreen from './components/common/AccessErrorScreen';
import ProtectedRoute from './components/common/ProtectedRoute';
import InsightsPageFallback from './components/production/InsightsPageFallback';

// Layouts
import AdminLayout from './components/layout/AdminLayout';
import ProductionLayout from './components/layout/ProductionLayout';

// Public
import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';

// Production pages
import ProductionHome from './pages/production/ProductionHome';
import StartTaskPage from './pages/production/StartTaskPage';
import ActiveTaskPage from './pages/production/ActiveTaskPage';
import RecentTasksPage from './pages/production/RecentTasksPage';
const ProductionInsightsPage = lazy(() => import('./pages/production/ProductionInsightsPage'));

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminTasksPage from './pages/admin/AdminTasksPage';
import ProductionReplayPage from './pages/admin/ProductionReplayPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminCategoriesPage from './pages/admin/AdminCategoriesPage';
import AdminTagsPage from './pages/admin/AdminTagsPage';
import AdminRoomsPage from './pages/admin/AdminRoomsPage';
import AdminLabourRatesPage from './pages/admin/AdminLabourRatesPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminFeedbackPage from './pages/admin/AdminFeedbackPage';

function RoleRedirect() {
  const { user, profile, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!profile || !profile.companyId) return <AccessErrorScreen />;
  if (profile.role === 'admin') return <Navigate to="/admin" replace />;
  return <Navigate to="/production" replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<RoleRedirect />} />
        <Route path="/app" element={<RoleRedirect />} />
        <Route
          path="/settings/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/production"
          element={
            <ProtectedRoute requireRole="production">
              <ProductionLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<ProductionHome />} />
          <Route path="start-task" element={<StartTaskPage />} />
          <Route path="active-task" element={<ActiveTaskPage />} />
          <Route path="recent" element={<RecentTasksPage />} />
          <Route
            path="insights"
            element={(
              <Suspense fallback={<InsightsPageFallback />}>
                <ProductionInsightsPage />
              </Suspense>
            )}
          />
        </Route>
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireRole="admin">
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="production-replay" element={<ProductionReplayPage />} />
          <Route path="tasks" element={<AdminTasksPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="task-categories" element={<AdminCategoriesPage />} />
          <Route path="task-tags" element={<AdminTagsPage />} />
          <Route path="rooms" element={<AdminRoomsPage />} />
          <Route path="labour-rates" element={<AdminLabourRatesPage />} />
          <Route path="settings"  element={<AdminSettingsPage />} />
          <Route path="feedback"  element={<AdminFeedbackPage />} />
        </Route>
        <Route path="*" element={<RoleRedirect />} />
      </Routes>
    </BrowserRouter>
  );
}
